import React, { useEffect } from "react";
import { HttpStatusCode } from "axios";
import { useNavigate } from "react-router-dom";

import { MANAGER_ROLE } from "../../../../consts/member";

import { useUserContext } from "../../../../contexts/userContext";

import {
  ManagerPushInfo,
  getManagerPush,
  updateManagerPush,
} from "../../../../services/pushServices";

import useFullScreenLoading from "../../../../hooks/useFullScreenLoading";

import { MyInfoConfigRow } from "..";
import StickyHeader from "../../../../components/Layout/StickyHeader";
import CommonSwitch from "../../../../components/Input/Switch";
import CommonButton from "../../../../components/Button";
import CommonInput from "../../../../components/Input/CommonInput";

type Props = {};

export default function MyInfoConfigPushManager(props: Props) {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = React.useState(false);
  const loadingComponent = useFullScreenLoading({
    isLoading,
    testId: "pushManager",
  });
  const [push, setPush] = React.useState<ManagerPushInfo>();
  const [user] = useUserContext();

  const loadManagerPush = async () => {
    setIsLoading(true);
    try {
      const res = await getManagerPush();
      if (res.status === HttpStatusCode.Ok) {
        setPush(res.data);
      }
    } catch (e: any) {
      console.log(e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClick = async (params: Partial<ManagerPushInfo>) => {
    setIsLoading(true);
    try {
      await updateManagerPush({ ...push, ...params } as ManagerPushInfo);
    } catch (e: any) {
      console.log(e);
    } finally {
      loadManagerPush();
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user.isLoading) return;
    if (user.role?.includes(MANAGER_ROLE.USER)) {
      navigate("/myinfo/config");
    }
    loadManagerPush();
  }, [user.isLoading]);

  return (
    <div>
      {loadingComponent}
      <StickyHeader
        title="서비스 이용알림 수신 설정"
        onBackClick={() => navigate("/myinfo/config")}
        className="px-5"
      />

      <MyInfoConfigRow
        subChildren={
          push?.donSturb === true && (
            <div className="p-5 pt-0 flex flex-col gap-2.5">
              <div className="text-sm text-brand-neutral-60 flex gap-10">
                <span className="whitespace-nowrap h-[50px] flex items-center">
                  시작시간
                </span>
                <CommonInput
                  value={push.donSturbStart}
                  className="w-full"
                  onChange={(text) =>
                    setPush(
                      (prev) =>
                        ({
                          ...prev,
                          donSturbStart: text
                            .replace(/[^\d]/, "")
                            .slice(0, 4)
                            .replace(
                              /(\d{0,2})(\d{0,2})/,
                              (_, g1, g2) => `${g1}${g2 ? ":" : ""}${g2}`
                            ),
                        } as ManagerPushInfo)
                    )
                  }
                  placeholder="11:00"
                  error={
                    /^(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/.test(
                      push.donSturbStart
                    ) || push.donSturbStart === null
                      ? undefined
                      : "유효하지 않은 시간입니다."
                  }
                />
              </div>
              <div className="text-sm text-brand-neutral-60 flex gap-10">
                <span className="whitespace-nowrap h-[50px] flex items-center">
                  종료시간
                </span>
                <CommonInput
                  value={push.donSturbEnd}
                  className="w-full"
                  onChange={(text) =>
                    setPush(
                      (prev) =>
                        ({
                          ...prev,
                          donSturbEnd: text
                            .replace(/[^\d]/, "")
                            .slice(0, 4)
                            .replace(
                              /(\d{0,2})(\d{0,2})/,
                              (_, g1, g2) => `${g1}${g2 ? ":" : ""}${g2}`
                            ),
                        } as ManagerPushInfo)
                    )
                  }
                  placeholder="07:00"
                  error={
                    /^(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/.test(
                      push.donSturbEnd
                    ) || push.donSturbEnd === null
                      ? undefined
                      : "유효하지 않은 시간입니다."
                  }
                />
              </div>
              <div className="pt-2.5 flex justify-between items-center">
                <div className="text-xs whitespace-pre-line">
                  {
                    "* 푸시알림 무음모드 시간 설정입니다.\n** 24h 단위로 입력해주세요."
                  }
                </div>
                <CommonButton
                  variant="tertiary-s"
                  onClick={() => {
                    handleClick({
                      donSturb: true,
                      donSturbStart: push?.donSturbStart,
                      donSturbEnd: push?.donSturbEnd,
                    });
                  }}
                  disabled={
                    /\d{2}:\d{2}/.test(push?.donSturbStart || "") === false ||
                    /\d{2}:\d{2}/.test(push?.donSturbEnd || "") === false
                  }
                >
                  시간 설정 저장
                </CommonButton>
              </div>
            </div>
          )
        }
      >
        <div>방해금지 모드</div>
        <CommonSwitch
          variant="black"
          value={push?.donSturb}
          testId="donSturb"
          onClick={() => {
            if (push?.donSturb !== true) {
              if (push?.donSturbEnd && push?.donSturbStart) {
                handleClick({ donSturb: !push?.donSturb });
              } else {
                setPush(
                  (prev) =>
                    ({
                      ...prev,
                      donSturb: !prev?.donSturb,
                    } as ManagerPushInfo)
                );
              }
            } else {
              handleClick({ donSturb: !push?.donSturb });
            }
          }}
        />
      </MyInfoConfigRow>

      <MyInfoConfigRow>
        <div>구내식당 문의 알림</div>
        <CommonSwitch
          variant="black"
          value={push?.restaurant}
          onClick={() => handleClick({ restaurant: !push?.restaurant })}
        />
      </MyInfoConfigRow>

      <MyInfoConfigRow
        subChildren={
          <div className="flex flex-col">
            <div className="px-5 flex gap-2.5 justify-between h-bottom-button items-center">
              <span>- 불편신고 미화 알림</span>
              <CommonSwitch
                variant="black"
                value={push?.incommodityCleaning}
                onClick={() =>
                  handleClick({
                    incommodityCleaning: !push?.incommodityCleaning,
                  })
                }
              />
            </div>
            <div className="px-5 flex gap-2.5 justify-between h-bottom-button items-center">
              <span>- 불편신고 시설 알림</span>
              <CommonSwitch
                variant="black"
                value={push?.incommodityFacility}
                onClick={() =>
                  handleClick({
                    incommodityFacility: !push?.incommodityFacility,
                  })
                }
              />
            </div>
            <div className="px-5 flex gap-2.5 justify-between h-bottom-button items-center">
              <span>- 불편신고 기타 알림</span>
              <CommonSwitch
                variant="black"
                value={push?.incommodityEtc}
                onClick={() =>
                  handleClick({ incommodityEtc: !push?.incommodityEtc })
                }
              />
            </div>
            <div className="px-5 flex gap-2.5 justify-between h-bottom-button items-center">
              <span>- 불편신고 총무 알림</span>
              <CommonSwitch
                variant="black"
                value={push?.incommodityManage}
                onClick={() =>
                  handleClick({ incommodityManage: !push?.incommodityManage })
                }
              />
            </div>
            <div className="px-5 flex gap-2.5 justify-between h-bottom-button items-center">
              <span>- 불편신고 통신 알림</span>
              <CommonSwitch
                variant="black"
                value={push?.incommodityCommunication}
                onClick={() =>
                  handleClick({
                    incommodityCommunication: !push?.incommodityCommunication,
                  })
                }
              />
            </div>
          </div>
        }
      >
        <div>불편신고 공통 알림</div>
        <CommonSwitch
          variant="black"
          value={push?.incommodityCommon}
          onClick={() =>
            handleClick({ incommodityCommon: !push?.incommodityCommon })
          }
        />
      </MyInfoConfigRow>

      <MyInfoConfigRow>
        <div>신청 서비스 알림</div>
        <CommonSwitch
          variant="black"
          value={push?.service}
          onClick={() => handleClick({ service: !push?.service })}
        />
      </MyInfoConfigRow>

      <MyInfoConfigRow>
        <div>대여하기 알림</div>
        <CommonSwitch
          variant="black"
          value={push?.rent}
          onClick={() => handleClick({ rent: !push?.rent })}
        />
      </MyInfoConfigRow>

      <MyInfoConfigRow>
        <div>공간예약 알림</div>
        <CommonSwitch
          variant="black"
          value={push?.reservation}
          onClick={() => handleClick({ reservation: !push?.reservation })}
        />
      </MyInfoConfigRow>

      <MyInfoConfigRow>
        <div>모니터링 알림</div>
        <CommonSwitch
          variant="black"
          value={push?.monitoring}
          onClick={() => handleClick({ monitoring: !push?.monitoring })}
        />
      </MyInfoConfigRow>
      <MyInfoConfigRow>
        <div>온도투표 알림</div>
        <CommonSwitch
          variant="black"
          value={push?.temperature}
          onClick={() => handleClick({ temperature: !push?.temperature })}
          testId="temperature"
        />
      </MyInfoConfigRow>
    </div>
  );
}
